import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

import {
  computeSynastry,
  getGifts,
  type SynastryCrossAspect,
  type SynastryReading,
} from '@/api/client';
import DateField from '@/components/DateField';
import TimeField from '@/components/TimeField';
import OrnateFrame from '@/components/OrnateFrame';
import PlaceAutocomplete from '@/components/PlaceAutocomplete';
import RichText from '@/components/RichText';
import { usePayment } from '@/hooks/usePayment';
import { useSubscription } from '@/hooks/useSubscription';
import { useUserStore } from '@/store/userStore';

/**
 * Synastry — relationship reading between the user (chart A, already
 * computed via /natal) and a partner whose data is entered inline
 * via the form below.
 *
 * Pricing: 200⭐, free for Priestess, lifetime cached per (user, partner
 * data hash). Re-opening the same partner is free.
 *
 * Gate: user MUST have their own natal chart computed first. If not,
 * we redirect them with a friendly nudge — synastry without your OWN
 * chart is meaningless.
 */

// Pretty labels for the result panel. Backend returns enum keys
// (sun/moon/.../trine/square...) — we map to display strings here.
const PLANET_LABELS_RU: Record<SynastryCrossAspect['planet_a'], string> = {
  sun: 'Солнце', moon: 'Луна', mercury: 'Меркурий', venus: 'Венера',
  mars: 'Марс', jupiter: 'Юпитер', saturn: 'Сатурн', uranus: 'Уран',
  neptune: 'Нептун', pluto: 'Плутон',
};
const PLANET_LABELS_EN: Record<SynastryCrossAspect['planet_a'], string> = {
  sun: 'Sun', moon: 'Moon', mercury: 'Mercury', venus: 'Venus',
  mars: 'Mars', jupiter: 'Jupiter', saturn: 'Saturn', uranus: 'Uranus',
  neptune: 'Neptune', pluto: 'Pluto',
};
const PLANET_GLYPHS: Record<SynastryCrossAspect['planet_a'], string> = {
  sun: '☉', moon: '☽', mercury: '☿', venus: '♀', mars: '♂',
  jupiter: '♃', saturn: '♄', uranus: '♅', neptune: '♆', pluto: '♇',
};
const ASPECT_LABELS_RU: Record<SynastryCrossAspect['aspect'], string> = {
  conjunction: 'соединение', opposition: 'оппозиция',
  trine: 'тригон', square: 'квадрат', sextile: 'секстиль',
};
const ASPECT_LABELS_EN: Record<SynastryCrossAspect['aspect'], string> = {
  conjunction: 'conjunction', opposition: 'opposition',
  trine: 'trine', square: 'square', sextile: 'sextile',
};

// Sign labels — needed for the partner's planet breakdown so the user
// can verify the AI's claims ("his Moon is in Libra") against actual
// computed placements.
type SignKey =
  | 'aries' | 'taurus' | 'gemini' | 'cancer' | 'leo' | 'virgo'
  | 'libra' | 'scorpio' | 'sagittarius' | 'capricorn'
  | 'aquarius' | 'pisces';

const SIGN_LABELS_RU: Record<SignKey, string> = {
  aries: 'Овен', taurus: 'Телец', gemini: 'Близнецы', cancer: 'Рак',
  leo: 'Лев', virgo: 'Дева', libra: 'Весы', scorpio: 'Скорпион',
  sagittarius: 'Стрелец', capricorn: 'Козерог', aquarius: 'Водолей',
  pisces: 'Рыбы',
};
const SIGN_LABELS_EN: Record<SignKey, string> = {
  aries: 'Aries', taurus: 'Taurus', gemini: 'Gemini', cancer: 'Cancer',
  leo: 'Leo', virgo: 'Virgo', libra: 'Libra', scorpio: 'Scorpio',
  sagittarius: 'Sagittarius', capricorn: 'Capricorn', aquarius: 'Aquarius',
  pisces: 'Pisces',
};

/** Pick a colour for the aspect badge — harmonious blue, hard red,
 *  neutral gold. Matches the natal-wheel aspect line palette. */
function aspectColour(aspect: SynastryCrossAspect['aspect']): string {
  if (aspect === 'trine' || aspect === 'sextile') return 'text-sky-300/90';
  if (aspect === 'square' || aspect === 'opposition') return 'text-rose-300/90';
  return 'text-gold-300/90';
}


export default function SynastryScreen() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language.startsWith('ru') ? 'ru' : 'en';
  const navigate = useNavigate();

  const user = useUserStore((s) => s.user);
  const [reading, setReading] = useState<SynastryReading | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasGift, setHasGift] = useState(false);

  const { pay, paying, payError } = usePayment('synastry');
  const { isPriestess } = useSubscription();

  // Form fields for the partner's birth data.
  const [partnerName, setPartnerName] = useState('');
  const [partnerDate, setPartnerDate] = useState('');
  const [partnerTime, setPartnerTime] = useState('');
  const [partnerPlace, setPartnerPlace] = useState('');
  const [partnerLat, setPartnerLat] = useState<number | null>(null);
  const [partnerLon, setPartnerLon] = useState<number | null>(null);

  useEffect(() => {
    getGifts()
      .then((g) => setHasGift(g.products.includes('synastry')))
      .catch(() => {});
  }, []);

  // Gate: own natal data must exist. We can't verify "has natal chart
  // row" from this side — the backend will return 409 if missing. But
  // we CAN check the necessary inputs the user must have filled in
  // their profile.
  const ownChartReady =
    !!user?.birth_date &&
    !!user?.birth_time &&
    user?.birth_lat != null &&
    user?.birth_lon != null;

  const formComplete =
    partnerName.trim().length >= 1 &&
    !!partnerDate &&
    /^\d{2}:\d{2}$/.test(partnerTime) &&
    partnerLat != null &&
    partnerLon != null;

  const planetLabel = (k: SynastryCrossAspect['planet_a']) =>
    lang === 'ru' ? PLANET_LABELS_RU[k] : PLANET_LABELS_EN[k];
  const aspectLabel = (k: SynastryCrossAspect['aspect']) =>
    lang === 'ru' ? ASPECT_LABELS_RU[k] : ASPECT_LABELS_EN[k];
  const signLabel = (s: string) =>
    lang === 'ru'
      ? (SIGN_LABELS_RU[s as SignKey] ?? s)
      : (SIGN_LABELS_EN[s as SignKey] ?? s);

  const run = async () => {
    setError(null);
    try {
      // Default partner TZ offset to browser's current — accurate when
      // partner lives in the same TZ as the buyer, ~best-effort otherwise.
      // The chart degrades gracefully for off-by-one-hour errors (ASC
      // shifts ~15° per hour), so this is good enough for an MVP. We
      // can add an explicit dropdown later if synastry sees real volume.
      const browserTzOffset = -new Date().getTimezoneOffset();
      const r = await computeSynastry({
        partner_name: partnerName.trim(),
        partner_birth_date: partnerDate,
        partner_birth_time: partnerTime,
        partner_birth_place: partnerPlace,
        partner_birth_lat: partnerLat!,
        partner_birth_lon: partnerLon!,
        partner_birth_tz_offset_minutes: browserTzOffset,
      });
      setReading(r);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const onSubmit = () => {
    if (busy || paying || !formComplete) return;
    if (isPriestess || hasGift) {
      setBusy(true);
      run().finally(() => setBusy(false));
      return;
    }
    pay(run);
  };

  const reset = () => {
    setReading(null);
    setError(null);
  };

  return (
    <main className="mx-auto max-w-md px-5 pb-24 pt-2">
      <h1 className="mb-1 text-center font-serif text-2xl text-gold-300">
        ✦ {t('synastry.title')}
      </h1>
      <p className="mb-6 text-center text-xs italic text-gold-300/60">
        {t('synastry.subtitle')}
      </p>

      <AnimatePresence mode="wait">
        {!reading ? (
          <motion.section
            key="form"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
          >
            {/* Atmospheric banner — reuse the natal hero so the brand
                stays consistent across paid premium screens. */}
            <div className="mb-4 overflow-hidden rounded-2xl border border-gold-500/30">
              <img
                src="/natal/hero.jpeg"
                alt=""
                className="block h-44 w-full object-cover"
                loading="lazy"
              />
            </div>

            {!ownChartReady ? (
              <OrnateFrame className="card-glow mb-4">
                <p className="mb-3 font-serif italic leading-relaxed text-gold-200">
                  {t('synastry.need_own_chart')}
                </p>
                <button
                  type="button"
                  onClick={() => navigate('/natal-chart')}
                  className="btn-primary w-full"
                >
                  {t('synastry.open_own_chart')}
                </button>
              </OrnateFrame>
            ) : (
              <>
                <OrnateFrame className="card-glow mb-4">
                  <p className="text-center font-serif italic leading-relaxed text-gold-200">
                    {t('synastry.description')}
                  </p>
                </OrnateFrame>

                <div className="space-y-3">
                  {/* Partner name */}
                  <label className="block">
                    <span className="mb-1 block text-[10px] uppercase tracking-widest text-gold-500/60">
                      {t('synastry.partner_name')}
                    </span>
                    <input
                      type="text"
                      value={partnerName}
                      onChange={(e) => setPartnerName(e.target.value)}
                      maxLength={64}
                      placeholder={t('synastry.partner_name_placeholder')}
                      className="w-full rounded-lg border border-gold-500/30
                                 bg-night-900/60 px-3 py-2 font-serif text-base text-gold-100
                                 outline-none transition focus:border-gold-400"
                    />
                  </label>

                  {/* Partner birth date */}
                  <label className="block">
                    <span className="mb-1 block text-[10px] uppercase tracking-widest text-gold-500/60">
                      {t('synastry.partner_birth_date')}
                    </span>
                    <DateField value={partnerDate} onChange={setPartnerDate} />
                  </label>

                  {/* Partner birth time */}
                  <label className="block">
                    <span className="mb-1 block text-[10px] uppercase tracking-widest text-gold-500/60">
                      {t('synastry.partner_birth_time')}
                    </span>
                    <TimeField
                      value={partnerTime}
                      onChange={setPartnerTime}
                    />
                  </label>

                  {/* Partner birth place */}
                  <PlaceAutocomplete
                    label={t('synastry.partner_birth_place')}
                    value={partnerPlace}
                    onChange={(place, lat, lon) => {
                      setPartnerPlace(place);
                      if (lat !== null) setPartnerLat(lat);
                      if (lon !== null) setPartnerLon(lon);
                    }}
                  />

                  {!formComplete && (partnerName || partnerDate || partnerTime || partnerPlace) && (
                    <p className="rounded-lg border border-gold-500/25 bg-night-900/40
                                  px-3 py-2 text-[11px] italic leading-snug text-gold-300/70">
                      ✦ {t('synastry.fill_all_fields')}
                    </p>
                  )}
                </div>

                <button
                  type="button"
                  onClick={onSubmit}
                  disabled={busy || paying || !formComplete}
                  className="btn-primary mt-5 w-full disabled:opacity-40"
                >
                  {paying || busy
                    ? t('payment.opening')
                    : isPriestess
                      ? t('synastry.open_priestess')
                      : hasGift
                        ? t('synastry.open_gift')
                        : t('synastry.open_for_stars', { stars: 200 })}
                </button>
                {isPriestess && (
                  <p className="mt-2 text-center text-[10px] uppercase tracking-widest text-gold-400/70">
                    ✦ {t('spread.included_priestess')}
                  </p>
                )}
                {(payError || error) && (
                  <p className="mt-3 text-center text-xs text-rose-400">
                    {payError ?? error}
                  </p>
                )}

                {/* What's inside teaser */}
                <ul className="mt-5 space-y-1.5 text-xs text-gold-300/70">
                  {[
                    'inside_vibe',
                    'inside_emotion',
                    'inside_romance',
                    'inside_support',
                    'inside_friction',
                    'inside_guidance',
                  ].map((k) => (
                    <li key={k} className="flex items-start gap-2">
                      <span className="text-gold-500/80">✦</span>
                      <span>{t(`synastry.${k}`)}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </motion.section>
        ) : (
          <motion.section
            key="result"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {reading.from_cache && (
              <div className="mb-3 rounded-lg border border-gold-500/30
                              bg-gold-500/10 px-3 py-2 text-[12px] leading-snug
                              text-gold-200">
                ✦ {t('synastry.from_cache')}
              </div>
            )}
            {reading.truncated && (
              <div className="mb-3 rounded-lg border border-rose-400/40
                              bg-rose-500/10 px-3 py-2.5 text-[12px] leading-snug
                              text-rose-200">
                <p>⚠ {t('synastry.truncated')}</p>
              </div>
            )}

            {/* Partner header — show whose chart we just compared with */}
            <OrnateFrame className="mb-4">
              <p className="text-center font-serif italic text-gold-200">
                {t('synastry.you_and')} <span className="font-bold text-gold-100">
                  {reading.metadata.partner_name}
                </span>
              </p>
              {reading.metadata.partner_birth_place && (
                <p className="mt-1 text-center text-[11px] text-gold-400/60">
                  {reading.metadata.partner_birth_place}
                </p>
              )}
            </OrnateFrame>

            {/* Summary chips — harmonious / challenging / neutral counts */}
            <div className="mb-4 grid grid-cols-3 gap-2">
              <div className="rounded-lg border border-sky-500/30 bg-sky-500/5 px-2 py-2 text-center">
                <p className="text-xl font-serif text-sky-200">
                  {reading.metadata.overlay.harmonious_count}
                </p>
                <p className="text-[9px] uppercase tracking-widest text-sky-300/70">
                  {t('synastry.harmonious')}
                </p>
              </div>
              <div className="rounded-lg border border-rose-500/30 bg-rose-500/5 px-2 py-2 text-center">
                <p className="text-xl font-serif text-rose-200">
                  {reading.metadata.overlay.challenging_count}
                </p>
                <p className="text-[9px] uppercase tracking-widest text-rose-300/70">
                  {t('synastry.challenging')}
                </p>
              </div>
              <div className="rounded-lg border border-gold-500/30 bg-gold-500/5 px-2 py-2 text-center">
                <p className="text-xl font-serif text-gold-200">
                  {reading.metadata.overlay.neutral_count}
                </p>
                <p className="text-[9px] uppercase tracking-widest text-gold-300/70">
                  {t('synastry.neutral')}
                </p>
              </div>
            </div>

            {/* Top aspects table — most defining contacts between the
                two charts, capped at 7 so the visual stays digestible. */}
            <OrnateFrame className="mb-4">
              <p className="mb-2 text-[10px] uppercase tracking-widest text-gold-500/70">
                {t('synastry.top_aspects')}
              </p>
              <ul className="space-y-1.5 text-sm">
                {reading.metadata.overlay.aspects.slice(0, 7).map((a, i) => (
                  <motion.li
                    key={`as-${i}`}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 + i * 0.10, duration: 0.35 }}
                    className="grid grid-cols-[auto_1fr_auto] items-center gap-2"
                  >
                    <span className="font-serif text-gold-300">
                      {PLANET_GLYPHS[a.planet_a]}
                    </span>
                    <span className="font-serif text-gold-200">
                      <span className="text-gold-300/80">
                        {planetLabel(a.planet_a)}
                      </span>
                      <span className={`mx-1.5 italic ${aspectColour(a.aspect)}`}>
                        {aspectLabel(a.aspect)}
                      </span>
                      <span className="text-gold-300/80">
                        {planetLabel(a.planet_b)}
                      </span>
                    </span>
                    <span className="text-[10px] text-gold-400/50">
                      {a.orb.toFixed(1)}°
                    </span>
                  </motion.li>
                ))}
              </ul>
            </OrnateFrame>

            {/* Partner's planet placements — added so the user can
                verify the AI's claims (e.g. "his Moon in Libra")
                against the actual computed chart, NOT have to take
                the AI's word for it. Same row layout as the natal
                chart's planet list for consistency. */}
            <OrnateFrame className="mb-4">
              <p className="mb-2 text-[10px] uppercase tracking-widest text-gold-500/70">
                {t('synastry.partner_chart', { name: reading.metadata.partner_name })}
              </p>
              <ul className="space-y-1.5 text-sm text-gold-200">
                {reading.metadata.chart_b.planets.map((p, i) => (
                  <motion.li
                    key={`pb-${p.planet}`}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    // Delay starts after the aspects cascade above
                    // (7 aspects × 0.10s + initial 0.3s ≈ 1.0s) so the
                    // two lists don't compete for the eye.
                    transition={{ delay: 1.1 + i * 0.08, duration: 0.35 }}
                    className="flex items-center gap-3"
                  >
                    <span className="w-6 font-serif text-base text-gold-300">
                      {PLANET_GLYPHS[p.planet]}
                    </span>
                    <span className="flex-1 font-serif">
                      <span className="text-gold-300">{planetLabel(p.planet)}</span>
                      <span className="text-gold-400/70"> · {signLabel(p.sign)}</span>
                      <span className="text-gold-400/60"> · {p.house}{t('natal.house_suffix')}</span>
                    </span>
                    {p.retrograde && (
                      <span className="text-[10px] uppercase tracking-widest text-rose-300/80">
                        R
                      </span>
                    )}
                  </motion.li>
                ))}
              </ul>
            </OrnateFrame>

            {/* AI interpretation — 6 sections of bold-headed text */}
            <OrnateFrame className="card-glow mb-4">
              <RichText
                text={reading.interpretation}
                className="font-serif italic leading-relaxed text-gold-200"
              />
            </OrnateFrame>

            <div className="mt-4 flex items-center justify-between gap-3">
              <button type="button" onClick={reset} className="nav-back-pill">
                <span>←</span>
                <span>{t('synastry.back')}</span>
              </button>
              <button
                type="button"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                aria-label={t('reading.scroll_to_top')}
                className="nav-scroll-top"
              >
                ↑
              </button>
            </div>
          </motion.section>
        )}
      </AnimatePresence>
    </main>
  );
}
